import React, { useState } from 'react';
import styled from 'styled-components';
import { IEntityCategoryFieldsFragment } from '../../utils/apollo/graphqlSchema';
import { useSaveCategories } from '../../utils/categories';
import { ICreatableReactSelectOption } from '../../utils/types';
import { PrimaryButton } from '../Button';
import Popover from '../Popover';
import CategoriesMenu from './CategoriesMenu';
import { defaultManagedOption } from './utils';

interface IProps {
  schemaId: string;
  isOpen: boolean;
  onClose: () => void;
  anchorEl: React.MutableRefObject<HTMLElement | null>;
  onApply: (selectedCategories: string[], isManaged: boolean) => void;
  entityCategories: IEntityCategoryFieldsFragment[];
  isManaged?: boolean;
}

const Container = styled.div`
  width: 320px;
`;
const MenuContainer = styled.div`
  margin-bottom: 8px;
  padding: 12px;
`;
const ApplyButton = styled(PrimaryButton)`
  width: 100%;
  height: 36px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;

const CategoriesPopover = ({
  schemaId,
  isOpen,
  onClose,
  anchorEl,
  onApply,
  entityCategories,
  isManaged: isManagedProp,
}: IProps) => {
  const [submitting, setSubmitting] = useState(false);

  const {
    categories,
    setCategories,
    prepareCategories,
    isEntityManaged,
    setIsEntityManaged,
  } = useSaveCategories(schemaId, entityCategories, isManagedProp);

  const handleChange = (values: ICreatableReactSelectOption[]) => {
    const managed = values.find(
      (value) => value.value === defaultManagedOption.value,
    );
    setIsEntityManaged(!!managed);
    setCategories(
      values.filter((value) => value.value !== defaultManagedOption.value),
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const resultCategories = await prepareCategories();

    onApply(resultCategories, isEntityManaged);
  };

  const categoriesWithManaged = isEntityManaged
    ? [defaultManagedOption, ...categories]
    : categories;

  return (
    <Popover isOpen={isOpen} close={onClose} anchor={anchorEl.current}>
      <Container>
        <MenuContainer>
          <CategoriesMenu
            schemaId={schemaId}
            selectedValues={categoriesWithManaged}
            onChange={handleChange}
          />
        </MenuContainer>
        <ApplyButton
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          trackContext="categories"
          trackAction="apply tags"
        >
          Apply Tags
        </ApplyButton>
      </Container>
    </Popover>
  );
};

export default CategoriesPopover;
